import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTicketInfo, validateTicket } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import Navbar from '../components/Navbar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorIcon from '@mui/icons-material/Error';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';

function TicketValidator() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const { colors } = useTheme();

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isStaff = user?.role === 'admin' || user?.role === 'portero';

  useEffect(() => {
    getTicketInfo(code)
      .then(res => {
        setTicketData(res.data);
        setLoading(false);
      })
      .catch(err => {
        if (err.response?.status === 404) setNotFound(true);
        setLoading(false);
      });
  }, [code]);

  const handleValidate = async () => {
    setValidating(true);
    try {
      await validateTicket(code);
      setTicketData(prev => ({
        ...prev,
        status: 'already_used',
        used: true,
        used_at: new Date().toISOString(),
      }));
    } catch (err) {
      if (err.response?.status === 409) {
        setTicketData(prev => ({ ...prev, status: 'already_used', used: true }));
      } else {
        alert('Error al validar la entrada');
      }
    }
    setValidating(false);
  };

  const isValid = ticketData?.status === 'valid';
  const isUsed = ticketData?.status === 'already_used';

  const statusColor = notFound ? '#888' : isUsed ? '#ff4444' : '#00cc66';
  const statusBg = notFound ? '#88888811' : isUsed ? '#ff444411' : '#00cc6611';

  if (loading) return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Navbar />
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <p style={{ color: colors.textSecondary }}>Verificando entrada...</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Navbar />
      <div style={{ padding: '1.5rem', maxWidth: '500px', margin: '0 auto' }}>

        {/* Estado de la entrada */}
        <div style={{
          backgroundColor: statusBg,
          border: `2px solid ${statusColor}`,
          borderRadius: '16px',
          padding: '2rem',
          textAlign: 'center',
          marginBottom: '1.5rem',
        }}>
          {notFound && <ErrorIcon style={{ fontSize: 72, color: '#888' }} />}
          {isUsed && <CancelIcon style={{ fontSize: 72, color: '#ff4444' }} />}
          {isValid && <CheckCircleIcon style={{ fontSize: 72, color: '#00cc66' }} />}

          <h1 style={{ color: statusColor, fontSize: '1.5rem', fontWeight: 'bold', margin: '0.8rem 0 0.4rem' }}>
            {notFound && 'Entrada no encontrada'}
            {isUsed && 'Entrada ya utilizada'}
            {isValid && 'Entrada valida'}
          </h1>
          <p style={{ color: colors.textSecondary, fontSize: '0.9rem', margin: 0 }}>
            {notFound && 'El codigo QR no corresponde a ninguna entrada.'}
            {isUsed && (ticketData?.used_at
              ? `Canjeada el ${new Date(ticketData.used_at).toLocaleString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
              : 'Esta entrada ya fue canjeada anteriormente.'
            )}
            {isValid && 'Esta entrada es autentica y no ha sido usada.'}
          </p>
        </div>

        {/* Datos del evento y asistente */}
        {ticketData && (
          <div style={{
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.border}`,
            borderRadius: '16px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
          }}>
            <h2 style={{ color: colors.text, fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '0.8rem' }}>
              {ticketData.event_name}
            </h2>
            <p style={{ color: colors.textSecondary, fontSize: '0.85rem', margin: '0.3rem 0' }}>
              📅 {new Date(ticketData.event_date).toLocaleDateString('es-ES', {
                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
              })}
            </p>
            <p style={{ color: colors.textSecondary, fontSize: '0.85rem', margin: '0.3rem 0 1rem' }}>
              📍 {ticketData.event_location}
            </p>
            <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '1rem' }}>
              <p style={{ color: colors.textSecondary, fontSize: '0.75rem', marginBottom: '0.2rem' }}>Asistente</p>
              <p style={{ color: colors.text, fontWeight: 'bold', fontSize: '1rem', marginBottom: '0.2rem' }}>
                {ticketData.attendee_name}
              </p>
              {ticketData.attendee_dni && (
                <p style={{ color: colors.textSecondary, fontSize: '0.85rem' }}>
                  DNI: {ticketData.attendee_dni}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Boton validar — solo staff, solo si es valida */}
        {isStaff && isValid && (
          <button
            onClick={handleValidate}
            disabled={validating}
            style={{
              width: '100%',
              backgroundColor: '#00cc66',
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              padding: '1rem',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              cursor: validating ? 'default' : 'pointer',
              opacity: validating ? 0.7 : 1,
              marginBottom: '0.8rem',
            }}
          >
            {validating ? 'Validando...' : 'Marcar como usada'}
          </button>
        )}

        {/* Ir al scanner — solo staff */}
        {isStaff && (
          <button
            onClick={() => navigate('/escanear')}
            style={{
              width: '100%',
              backgroundColor: 'transparent',
              border: `1px solid ${colors.border}`,
              color: colors.textSecondary,
              borderRadius: '12px',
              padding: '0.8rem',
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
            }}
          >
            <QrCodeScannerIcon style={{ fontSize: 18 }} /> Ir al escaner de camara
          </button>
        )}

      </div>
    </div>
  );
}

export default TicketValidator;